import app from '../app/app';
import config from '../config/config';
import * as Constants from '../config/constants';
import {getLocale} from '../intl';
import {clearSessionToken} from './session-token';

const RESTART_GUARD_KEY = 'ibe_session_restart';

let handling = false;

const isInvalidSessionStatus = (status) => status === 401 || status === 403;

const isSessionGoneResponse = (response) => {
    if (!response || typeof response.status !== 'number') {
        return false;
    }

    if (response.status < 400 || response.status >= 500) {
        return false;
    }

    const headers = response.headers;
    const contentType = (headers && typeof headers.get === 'function') ? headers.get('content-type') : '';

    return typeof contentType === 'string' && contentType.indexOf('application/problem+json') === 0;
};

const getSessionStorage = () => {
    try {
        return window.sessionStorage || null;
    } catch (e) {
        return null;
    }
};

const restartGuardIsSet = () => {
    const storage = getSessionStorage();
    if (!storage) {
        return false;
    }
    try {
        return storage.getItem(RESTART_GUARD_KEY) !== null;
    } catch (e) {
        return false;
    }
};

const setRestartGuard = () => {
    const storage = getSessionStorage();
    if (!storage) {
        return false;
    }
    try {
        storage.setItem(RESTART_GUARD_KEY, `${Date.now()}`);
        return true;
    } catch (e) {
        return false;
    }
};

const clearRestartGuard = () => {
    const storage = getSessionStorage();
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(RESTART_GUARD_KEY);
    } catch (e) {
        //
    }
};

const isRestarting = () => handling;

const buildRestartUrl = () => {
    const storage = app && app.ibeController ? app.ibeController.ibeStorage : null;
    const allParams = storage ? storage.get(Constants.ALL_QUERY_PARAMETER) : '';
    const initialParameter = storage ? storage.get(Constants.INITIAL_PARAMETER) : null;

    const params = new URLSearchParams(allParams || '');
    params.delete(Constants.INITIAL_PARAMETER_API_SID);

    if (!params.get(Constants.INITIAL_PARAMETER_TRIPCODE) && initialParameter && initialParameter[Constants.INITIAL_PARAMETER_TRIPCODE]) {
        params.set(Constants.INITIAL_PARAMETER_TRIPCODE, initialParameter[Constants.INITIAL_PARAMETER_TRIPCODE]);
        params.set(Constants.INITIAL_PARAMETER_EPACKAGECODE, initialParameter[Constants.INITIAL_PARAMETER_EPACKAGECODE]);
    }

    if (!params.get('locale')) {
        params.set('locale', getLocale());
    }

    const query = params.toString();
    return query ? `${config.root}?${query}` : config.root;
};

const handleInvalidSession = () => {
    if (handling) {
        return false;
    }
    handling = true;

    const restartUrl = buildRestartUrl();

    clearSessionToken();

    if (app && app.ibeController && app.ibeController.ibeStorage) {
        app.ibeController.ibeStorage.deleteStorage();
    }

    window.location.href = restartUrl;
    return true;
};

const handleInvalidSessionResponse = (response) => {
    if (!response || !isInvalidSessionStatus(response.status)) {
        return false;
    }

    handleInvalidSession();
    return true;
};

const canRestartAfterSessionExpiry = (urlParams) => {
    if (handling || restartGuardIsSet() || !getSessionStorage()) {
        return false;
    }

    if (urlParams && urlParams.get(Constants.INITIAL_PARAMETER_TRIPCODE)) {
        return false;
    }

    const ibeStatus = app && app.ibeController ? app.ibeController.ibeStatus : null;
    if (ibeStatus && ibeStatus.restoreBooking === true) {
        return false;
    }

    const storage = app && app.ibeController ? app.ibeController.ibeStorage : null;
    if (!storage) {
        return false;
    }

    if (storage.get(Constants.CONFIRM_STATUS) === 'true') {
        return false;
    }

    const initialParameter = storage.get(Constants.INITIAL_PARAMETER);
    const tripCode = initialParameter ? initialParameter[Constants.INITIAL_PARAMETER_TRIPCODE] : null;

    return Boolean(tripCode) && tripCode !== 'used';
};

const restartAfterSessionExpiry = (urlParams) => {
    if (!canRestartAfterSessionExpiry(urlParams)) {
        return false;
    }

    setRestartGuard();

    return handleInvalidSession();
};

export {
    buildRestartUrl,
    canRestartAfterSessionExpiry,
    clearRestartGuard,
    handleInvalidSession,
    handleInvalidSessionResponse,
    isInvalidSessionStatus,
    isRestarting,
    isSessionGoneResponse,
    restartAfterSessionExpiry
};
